/**
 * Multiple Mux playback IDs per country for A/B performance comparison.
 *
 * Storage in `site_settings.international_home_mux_by_country` (JSONB):
 *   - Legacy:  { "US": "playbackId..." }
 *   - Multi:   { "US": { "variants": [ { key, playbackId, label, weight, enabled }, ... ] } }
 *
 * Runtime: sticky weighted pick (localStorage) so the same visitor keeps one
 * variant; PostHog/funnel get mux_variant_key + mux_variant_label + playback_id.
 */

const MUX_PLAYBACK_ID_RE = /^[A-Za-z0-9_-]{8,128}$/;

export function isValidMuxPlaybackId(value: unknown): value is string {
  return typeof value === "string" && MUX_PLAYBACK_ID_RE.test(value.trim());
}

export type MuxCountryVariant = {
  /** Stable analytics id (a, b, hero-v2). */
  key: string;
  playbackId: string;
  /** Human label in admin. */
  label: string;
  /** Relative weight (≥1). Higher = more traffic. */
  weight: number;
  enabled: boolean;
};

/** Per-country entry: one or more variants. */
export type MuxCountryConfig = {
  variants: MuxCountryVariant[];
};

/** Map ISO2 → legacy string | multi-variant config. */
export type InternationalHomeMuxByCountryRaw = Record<
  string,
  string | MuxCountryConfig
>;

/** Flattened map for callers that only need a single playback string. */
export type InternationalHomeMuxByCountry = Record<string, string>;

const KEY_RE = /^[a-zA-Z0-9_-]{1,32}$/;

function slugKey(raw: string, fallback: string): string {
  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
  return KEY_RE.test(s) ? s : fallback;
}

export function normalizeMuxCountryVariant(
  raw: unknown,
  index: number,
): MuxCountryVariant | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const playbackId =
    typeof o.playbackId === "string"
      ? o.playbackId.trim()
      : typeof o.playback_id === "string"
        ? o.playback_id.trim()
        : typeof o.id === "string" && isValidMuxPlaybackId(o.id)
          ? o.id.trim()
          : "";
  if (!isValidMuxPlaybackId(playbackId)) return null;
  const keyRaw =
    typeof o.key === "string"
      ? o.key
      : typeof o.variant_key === "string"
        ? o.variant_key
        : "";
  const key = slugKey(keyRaw, `v${index + 1}`);
  const label =
    typeof o.label === "string" && o.label.trim()
      ? o.label.trim().slice(0, 80)
      : `Variante ${key}`;
  const weightRaw = Number(o.weight);
  const weight =
    Number.isFinite(weightRaw) && weightRaw > 0
      ? Math.min(1000, Math.round(weightRaw))
      : 50;
  const enabled = o.enabled === false ? false : true;
  return { key, playbackId, label, weight, enabled };
}

/**
 * Normalize one country value (string | config) → config with variants[].
 * Returns null if empty / invalid.
 */
export function parseMuxCountryConfig(raw: unknown): MuxCountryConfig | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const pid = raw.trim();
    if (!isValidMuxPlaybackId(pid)) return null;
    return {
      variants: [
        {
          key: "default",
          playbackId: pid,
          label: "Principal",
          weight: 100,
          enabled: true,
        },
      ],
    };
  }
  if (Array.isArray(raw)) {
    const variants = raw
      .map((v, i) => normalizeMuxCountryVariant(v, i))
      .filter((v): v is MuxCountryVariant => Boolean(v));
    if (!variants.length) return null;
    return dedupeVariantKeys(variants);
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    // Single object that looks like a variant
    if (o.playbackId || o.playback_id) {
      const one = normalizeMuxCountryVariant(o, 0);
      return one ? { variants: [one] } : null;
    }
    const list = Array.isArray(o.variants) ? o.variants : null;
    if (!list) return null;
    const variants = list
      .map((v, i) => normalizeMuxCountryVariant(v, i))
      .filter((v): v is MuxCountryVariant => Boolean(v));
    if (!variants.length) return null;
    return dedupeVariantKeys(variants);
  }
  return null;
}

function dedupeVariantKeys(variants: MuxCountryVariant[]): MuxCountryConfig {
  const seen = new Set<string>();
  const out: MuxCountryVariant[] = [];
  for (const v of variants) {
    let k = v.key;
    let n = 2;
    while (seen.has(k)) {
      k = `${v.key}${n}`.slice(0, 32);
      n += 1;
    }
    seen.add(k);
    out.push({ ...v, key: k });
  }
  return { variants: out };
}

/**
 * Parse full map from admin/API. Accepts legacy strings and multi-variant objects.
 * Stored shape for multi stays as { variants: [...] }; single can stay string on save.
 */
export function parseInternationalHomeMuxByCountryRich(
  raw: unknown,
): InternationalHomeMuxByCountryRaw {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return {};
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  const out: InternationalHomeMuxByCountryRaw = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const code = String(k).trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const cfg = parseMuxCountryConfig(v);
    if (!cfg) continue;
    // Persist compact: single enabled variant → string; multi → object
    const enabled = cfg.variants.filter(
      (x) => x.enabled && isValidMuxPlaybackId(x.playbackId),
    );
    if (enabled.length === 1 && cfg.variants.length === 1) {
      out[code] = enabled[0].playbackId;
    } else {
      out[code] = cfg;
    }
  }
  return out;
}

/** Legacy flat map: first enabled variant per country (for simple callers). */
export function flattenMuxByCountryToPlaybackMap(
  rich: InternationalHomeMuxByCountryRaw,
): InternationalHomeMuxByCountry {
  const out: InternationalHomeMuxByCountry = {};
  for (const [code, entry] of Object.entries(rich)) {
    const cfg = parseMuxCountryConfig(entry);
    if (!cfg) continue;
    const first = cfg.variants.find(
      (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
    );
    if (first) out[code] = first.playbackId;
  }
  return out;
}

/**
 * Backward-compatible parse used by most of the codebase:
 * returns Record<country, playbackId> using the first enabled variant.
 */
export function parseInternationalHomeMuxByCountry(
  raw: unknown,
): InternationalHomeMuxByCountry {
  return flattenMuxByCountryToPlaybackMap(
    parseInternationalHomeMuxByCountryRich(raw),
  );
}

export type ResolvedMuxVariant = {
  playbackId: string;
  variantKey: string;
  variantLabel: string;
  variantCount: number;
  /** country_admin | country_admin_ab */
  source: "country_admin" | "country_admin_ab";
};

function hashSticky(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function storageKey(country: string): string {
  return `ih_mux_variant:${country.toUpperCase()}`;
}

/**
 * Sticky weighted pick among enabled variants for a country.
 * SSR-safe: without window, picks first enabled (deterministic).
 */
export function pickMuxCountryVariant(
  cfg: MuxCountryConfig,
  stickySeed?: string | null,
): MuxCountryVariant | null {
  const enabled = cfg.variants.filter(
    (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
  );
  if (!enabled.length) return null;
  if (enabled.length === 1) return enabled[0];

  const total = enabled.reduce((s, v) => s + Math.max(1, v.weight), 0);
  const seed = stickySeed || "default";
  let bucket = hashSticky(seed) % total;
  for (const v of enabled) {
    bucket -= Math.max(1, v.weight);
    if (bucket < 0) return v;
  }
  return enabled[enabled.length - 1];
}

/**
 * Resolve playback + variant for country from rich map.
 * Uses localStorage sticky assignment when available.
 */
export function resolveInternationalHomeMuxVariantForCountry(
  country: string | null | undefined,
  byCountry: InternationalHomeMuxByCountryRaw | Record<string, string> | null | undefined,
  opts?: { forceVariantKey?: string | null; stickyId?: string | null },
): ResolvedMuxVariant | null {
  const code = typeof country === "string" ? country.trim().toUpperCase() : "";
  if (!code || !byCountry) return null;
  const entry = (byCountry as Record<string, unknown>)[code];
  const cfg = parseMuxCountryConfig(entry);
  if (!cfg) return null;

  const enabled = cfg.variants.filter(
    (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
  );
  if (!enabled.length) return null;

  if (opts?.forceVariantKey) {
    const forced = enabled.find((v) => v.key === opts.forceVariantKey);
    if (forced) {
      return {
        playbackId: forced.playbackId,
        variantKey: forced.key,
        variantLabel: forced.label,
        variantCount: enabled.length,
        source: enabled.length > 1 ? "country_admin_ab" : "country_admin",
      };
    }
  }

  let stickySeed = opts?.stickyId || null;
  if (typeof window !== "undefined") {
    try {
      const sk = storageKey(code);
      const existing = window.localStorage.getItem(sk);
      if (existing && enabled.some((v) => v.key === existing)) {
        const v = enabled.find((x) => x.key === existing)!;
        return {
          playbackId: v.playbackId,
          variantKey: v.key,
          variantLabel: v.label,
          variantCount: enabled.length,
          source: enabled.length > 1 ? "country_admin_ab" : "country_admin",
        };
      }
      // New assignment
      stickySeed = stickySeed || `${code}:${Date.now()}:${Math.random()}`;
      const picked = pickMuxCountryVariant(cfg, stickySeed);
      if (picked) {
        window.localStorage.setItem(sk, picked.key);
        return {
          playbackId: picked.playbackId,
          variantKey: picked.key,
          variantLabel: picked.label,
          variantCount: enabled.length,
          source: enabled.length > 1 ? "country_admin_ab" : "country_admin",
        };
      }
    } catch {
      /* private mode */
    }
  }

  const picked = pickMuxCountryVariant(cfg, stickySeed || code);
  if (!picked) return null;
  return {
    playbackId: picked.playbackId,
    variantKey: picked.key,
    variantLabel: picked.label,
    variantCount: enabled.length,
    source: enabled.length > 1 ? "country_admin_ab" : "country_admin",
  };
}

/** For admin UI: always array of variants for a country. */
export function getCountryVariantsList(
  byCountry: InternationalHomeMuxByCountryRaw | Record<string, string> | null | undefined,
  country: string,
): MuxCountryVariant[] {
  const code = country.trim().toUpperCase();
  if (!byCountry) return [];
  const cfg = parseMuxCountryConfig((byCountry as Record<string, unknown>)[code]);
  return cfg?.variants ?? [];
}

/** Serialize for admin save: rich map. */
export function serializeMuxByCountryRich(
  rich: InternationalHomeMuxByCountryRaw,
): Record<string, string | MuxCountryConfig> {
  return parseInternationalHomeMuxByCountryRich(rich);
}

export function emptyVariant(index = 0): MuxCountryVariant {
  return {
    key: `v${index + 1}`,
    playbackId: "",
    label: `Variante ${String.fromCharCode(65 + (index % 26))}`,
    weight: 50,
    enabled: true,
  };
}
